import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  User,
  Award,
  Clock,
  Sparkles,
  FolderOpen,
  Mail,
  MapPin,
  Phone,
  Linkedin,
  Github,
  Globe,
  Star,
  Trophy,
  Zap,
  CheckCircle2,
  ArrowRight,
  Share2,
  Loader,
  Calendar,
  Target,
  Edit,
  Upload,
  X,
  Image as ImageIcon,
  FileText,
  FileCode,
  FileSpreadsheet,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  Eye,
  ExternalLink,
  Download,
  Link as LinkIcon,
  BrainCircuit,
  Shield,
} from 'lucide-react';
import { getTaskerProfile } from '../api/taskerProfiles';
import { TaskerProfile as TaskerProfileType, Task } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { supabase, TABLES } from '../api/db';
import { uploadProfilePhoto, deleteProfilePhoto } from '../api/storage';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import LinkPreviewCard from '../components/LinkPreviewCard';
import { computeUserRank, UserRankData } from '../api/ranking';
import RankBadge from '../components/RankBadge';

const Tasker: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<TaskerProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  const [userTasks, setUserTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [rankData, setRankData] = useState<UserRankData | null>(null);
  const [rankLoading, setRankLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const isOwnProfile = currentUser?.uid === userId;

  useEffect(() => {
    loadProfile();
    loadUserTasks();
    if (userId) {
      setRankLoading(true);
      computeUserRank(userId)
        .then(setRankData)
        .catch((err) => console.error('Error computing rank:', err))
        .finally(() => setRankLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadUserTasks = async () => {
    if (!userId) return;
    try {
      setLoadingTasks(true);
      const tasksData: Task[] = [];

      const [{ data: marketRows }, { data: hatchRows }] = await Promise.all([
        supabase.from(TABLES.TASKS).select('*').eq('user_id', userId).eq('visibility', 'public').limit(50),
        supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('user_id', userId).eq('visibility', 'public').limit(50),
      ]);
      (marketRows ?? []).forEach((r) => tasksData.push(r as unknown as Task));
      (hatchRows ?? []).forEach((r) => {
        const row = r as Record<string, unknown>;
        if (!tasksData.some(t => t.id === row['id'])) tasksData.push(row as unknown as Task);
      });

      tasksData.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });

      setUserTasks(tasksData);
    } catch (err) {
      console.error('Error loading user tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    if (profile && isOwnProfile) {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, isOwnProfile]);

  const loadProfile = async () => {
    if (!userId) {
      setError('No user ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getTaskerProfile(userId);
      if (data) {
        setProfile(data);
        if (data.personalInfo.profilePhoto) {
          setImagePreview(data.personalInfo.profilePhoto);
        }
      } else {
        setError('Profile not found');
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    if (!profile) return;

    try {
      setLoadingAnalytics(true);
      const { data: dashData } = await supabase
        .from(TABLES.TASKER_DASHBOARD_DATA)
        .select('*')
        .eq('user_id', profile.id)
        .maybeSingle();
      if (dashData) setDashboardData((dashData as Record<string, unknown>)['analytics'] ?? dashData);
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share && profile) {
      try {
        await navigator.share({
          title: `${profile.personalInfo.displayName} - GNK Continuum Profile`,
          text: `Check out ${profile.personalInfo.displayName}'s profile on GNK Continuum`,
          url,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!currentUser || !selectedImage || !profile) return;

    try {
      setUploadingPhoto(true);
      const downloadURL = await uploadProfilePhoto(currentUser.uid, selectedImage);

      await supabase
        .from(TABLES.TASKER_PROFILES)
        .update({
          personal_info: { ...profile.personalInfo, profilePhoto: downloadURL },
          updated_at: new Date().toISOString(),
        })
        .eq('id', profile.id);

      await loadProfile();
      setShowEditModal(false);
      setSelectedImage(null);
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoRemove = async () => {
    if (!currentUser || !profile?.personalInfo.profilePhoto) return;

    try {
      setUploadingPhoto(true);

      try {
        await deleteProfilePhoto(currentUser.uid);
      } catch (err) {
        console.log('Photo not found in storage, continuing...');
      }

      const { profilePhoto: _removed, ...personalInfoWithoutPhoto } = profile.personalInfo;

      await supabase
        .from(TABLES.TASKER_PROFILES)
        .update({
          personal_info: personalInfoWithoutPhoto,
          updated_at: new Date().toISOString(),
        })
        .eq('id', profile.id);

      await loadProfile();
      setImagePreview(null);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error removing photo:', error);
      alert('Failed to remove photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Badge component for links
  const LinkBadge: React.FC<{ url: string; label: string; icon?: React.ReactNode }> = ({ url, label, icon }) => (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-tech-cyan/50 transition-all group"
    >
      {icon || <LinkIcon className="w-4 h-4 text-tech-cyan" />}
      <span className="text-sm font-medium">{label}</span>
      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
    </a>
  );

  // File badge component
  const FileBadge: React.FC<{ url: string; fileName: string; type: 'csv' | 'py' | 'pdf' | 'image' }> = ({ url, fileName, type }) => {
    const icons = {
      csv: FileSpreadsheet,
      py: FileCode,
      pdf: FileText,
      image: ImageIcon,
    };
    const colors = {
      csv: 'text-green-400',
      py: 'text-blue-400',
      pdf: 'text-red-400',
      image: 'text-violet-400',
    };
    const Icon = icons[type];
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-tech-cyan/50 transition-all group"
      >
        <Icon className={`w-4 h-4 ${colors[type]}`} />
        <span className="text-xs font-medium truncate max-w-[150px]">{fileName}</span>
        <Download className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </a>
    );
  };

  // ── Loading State ──
  if (loading) {
    return (
      <div className="min-h-screen bg-tech-dark flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 text-tech-cyan animate-spin mx-auto mb-4" />
          <p className="text-tech-text-muted text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  // ── Error State ──
  if (error || !profile) {
    return (
      <div className="min-h-screen bg-tech-dark flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-3xl font-bold mb-2 text-tech-text">Profile Not Found</h1>
          <p className="text-tech-text-muted mb-6">{error || 'This profile does not exist or is not available.'}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-tech-blue to-tech-cyan text-white font-semibold hover:opacity-90 transition-opacity"
          >
            <ArrowRight className="w-5 h-5" />
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  const displayName = profile.personalInfo.displayName;
  const avatarInitial = displayName.charAt(0).toUpperCase();
  const completionPercentage = profile.completionPercentage;
  const profilePhoto = profile.personalInfo.profilePhoto || imagePreview;

  const aiShowcase = profile.aiBuilderShowcase;
  const hasAiShowcase =
    !!aiShowcase &&
    !!(
      (aiShowcase.publicTasksStory && aiShowcase.publicTasksStory.trim()) ||
      aiShowcase.aiToolExperiences?.some((e) => (e.toolName && e.toolName.trim()) || (e.story && e.story.trim())) ||
      (aiShowcase.featuredLinks && aiShowcase.featuredLinks.length > 0)
    );

  const getProjectFiles = (project: any) => {
    const files: Array<{ url: string; fileName: string; type: 'csv' | 'py' | 'pdf' | 'image' }> = [];

    if (project.images && Array.isArray(project.images)) {
      project.images.forEach((img: string, idx: number) => {
        files.push({ url: img, fileName: `Image ${idx + 1}`, type: 'image' });
      });
    }

    if (project.url) {
      const url = project.url.toLowerCase();
      if (url.includes('.csv')) files.push({ url: project.url, fileName: 'Data File', type: 'csv' });
      if (url.includes('.py')) files.push({ url: project.url, fileName: 'Code File', type: 'py' });
      if (url.includes('.pdf')) files.push({ url: project.url, fileName: 'Documentation', type: 'pdf' });
    }

    return files;
  };

  const rankGlow = rankData ? rankData.rank.glowColor : 'rgba(34,211,238,0.3)';

  return (
    <div className="min-h-screen bg-tech-dark text-tech-text relative overflow-hidden">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-40">
        <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-tech-cyan/15 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10">
        {/* ── Premium Hero ── */}
        <div className="relative border-b border-white/8 overflow-hidden">
          {/* Hero gradient */}
          <div
            className="absolute inset-0 opacity-60"
            style={{
              background: `radial-gradient(ellipse at 30% 50%, ${rankGlow}, transparent 70%), linear-gradient(135deg, rgba(34,211,238,0.06) 0%, rgba(99,102,241,0.06) 50%, rgba(167,139,250,0.06) 100%)`,
            }}
          />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(34,211,238,0.08),transparent_60%)]" />

          <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-20">
            <div className="flex flex-col md:flex-row items-start gap-8 md:gap-12">
              {/* Avatar */}
              <div className="shrink-0 relative group">
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt={displayName}
                    className="w-32 h-32 md:w-44 md:h-44 rounded-[2rem] object-cover border-2 border-tech-cyan/40"
                    style={{ boxShadow: `0 0 50px ${rankGlow}, 0 0 100px ${rankGlow}` }}
                  />
                ) : (
                  <div
                    className="w-32 h-32 md:w-44 md:h-44 rounded-[2rem] bg-gradient-to-br from-tech-cyan to-blue-600 flex items-center justify-center text-5xl md:text-6xl font-bold text-white"
                    style={{ boxShadow: `0 0 50px ${rankGlow}, 0 0 100px ${rankGlow}` }}
                  >
                    {avatarInitial}
                  </div>
                )}
                {profile.status === 'active' && (
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-green-500 border-4 border-tech-dark flex items-center justify-center shadow-lg">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                )}
                {isOwnProfile && (
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="absolute -bottom-2 -left-2 w-10 h-10 rounded-full bg-tech-cyan hover:bg-tech-cyan/80 border-2 border-tech-dark flex items-center justify-center shadow-lg transition-all opacity-0 group-hover:opacity-100"
                    title="Edit Profile Photo"
                  >
                    <Edit className="w-4 h-4 text-tech-darker" />
                  </button>
                )}

                {/* Rank badge under avatar */}
                {rankData && !rankLoading && (
                  <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <RankBadge rankData={rankData} size="sm" />
                  </div>
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-1 mt-6 md:mt-0">
                {/* Name + Title */}
                <div className="flex flex-wrap items-start gap-3 mb-2">
                  <h1 className="text-4xl md:text-5xl font-bold animated-gradient-text tracking-tight">
                    {displayName}
                  </h1>
                  {profile.isTaskAchieverEligible && (
                    <span className="mt-1 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-xs font-bold flex items-center gap-1 text-white">
                      <Trophy className="w-3 h-3" />
                      TaskAchiever
                    </span>
                  )}
                </div>
                {profile.professionalInfo.title && (
                  <p className="text-xl text-tech-text-muted mb-3">{profile.professionalInfo.title}</p>
                )}
                {profile.personalInfo.bio && (
                  <p className="text-tech-text-muted leading-relaxed mb-5 max-w-xl line-clamp-3">
                    {profile.personalInfo.bio}
                  </p>
                )}

                {/* Location / availability chips */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {profile.personalInfo.location && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/8 text-sm text-tech-text-muted">
                      <MapPin className="w-4 h-4" />
                      {profile.personalInfo.location}
                    </div>
                  )}
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/8 text-sm text-tech-text-muted">
                    <Clock className="w-4 h-4" />
                    <span className="capitalize">{profile.workPreferences.availability.replace('-', ' ')}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/8 text-sm text-tech-text-muted">
                    <Globe className="w-4 h-4" />
                    <span className="capitalize">{profile.workPreferences.location.replace('-', ' ')}</span>
                  </div>
                  {completionPercentage && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/8 text-sm text-tech-text-muted">
                      <Target className="w-4 h-4" />
                      {completionPercentage}% profile
                    </div>
                  )}
                </div>

                {/* CTAs */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-tech-blue to-tech-cyan text-white font-semibold text-sm hover:opacity-90 transition-opacity"
                  >
                    <Share2 className="w-4 h-4" />
                    {shareCopied ? 'Copied!' : 'Share Profile'}
                  </button>
                  {isOwnProfile && (
                    <button
                      onClick={() => navigate('/tasker-meta')}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-tech-cyan/30 bg-tech-cyan/5 text-tech-cyan font-semibold text-sm hover:bg-tech-cyan/10 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Profile
                    </button>
                  )}
                  {profile.personalInfo.phone && (
                    <a
                      href={`tel:${profile.personalInfo.phone}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 bg-white/5 text-tech-text-muted font-semibold text-sm hover:bg-white/10 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </a>
                  )}
                  <a
                    href={`mailto:${profile.userEmail}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 bg-white/5 text-tech-text-muted font-semibold text-sm hover:bg-white/10 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats Bar ── */}
        <div className="border-b border-white/8 bg-tech-elevated/40 backdrop-blur-md">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Public Tasks', value: userTasks.length, icon: Target, color: 'text-cyan-400' },
                { label: 'Prompts Posted', value: rankData?.promptCount ?? '—', icon: Zap, color: 'text-violet-400' },
                { label: 'Rank Score', value: rankData ? rankData.score.toLocaleString() : '—', icon: Trophy, color: 'text-amber-400' },
                { label: 'Tasks Completed', value: rankData?.completedTaskCount ?? '—', icon: CheckCircle2, color: 'text-green-400' },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-3 p-3 rounded-xl bg-white/4 border border-white/6">
                  <stat.icon className={`w-5 h-5 ${stat.color} shrink-0`} />
                  <div>
                    <div className="text-lg font-bold text-tech-text leading-none">{stat.value}</div>
                    <div className="text-xs text-tech-text-muted mt-0.5">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Main Content ── */}
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-8">
              {/* ── Rank Showcase ── */}
              {rankLoading && (
                <div className="p-8 rounded-3xl glass-panel border border-tech-cyan/20 flex items-center gap-3">
                  <Loader className="w-5 h-5 text-tech-cyan animate-spin" />
                  <span className="text-tech-text-muted">Loading rank data...</span>
                </div>
              )}
              {rankData && !rankLoading && (
                <div
                  className={`p-8 rounded-3xl bg-gradient-to-br ${rankData.rank.color} border border-white/10 glass-panel`}
                  style={{ boxShadow: `0 0 60px ${rankData.rank.glowColor}` }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <Trophy className="w-6 h-6 text-amber-400" />
                    <h2 className="text-xl font-bold">Rank Showcase</h2>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-6">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-20 h-20 rounded-2xl flex items-center justify-center text-5xl"
                        style={{ background: rankData.rank.glowColor, boxShadow: `0 0 30px ${rankData.rank.glowColor}` }}
                      >
                        {rankData.rank.emoji}
                      </div>
                      <div>
                        <div className="text-3xl font-bold text-tech-text">{rankData.rank.title}</div>
                        <div className="text-sm text-tech-text-muted italic mt-1">"{rankData.rank.description}"</div>
                      </div>
                    </div>
                    <div className="sm:ml-auto text-right">
                      <div className="text-4xl font-bold text-tech-cyan">{rankData.score.toLocaleString()}</div>
                      <div className="text-sm text-tech-text-muted">rank score</div>
                    </div>
                  </div>

                  {rankData.nextRank && (
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-2 text-sm">
                        <span className="text-tech-text-muted">
                          Next: {rankData.nextRank.emoji} {rankData.nextRank.title} ({rankData.nextRank.minScore.toLocaleString()} pts)
                        </span>
                        <span className="text-tech-cyan font-semibold">{rankData.progressToNext}%</span>
                      </div>
                      <div className="w-full h-3 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-tech-cyan to-blue-400 transition-all duration-700"
                          style={{ width: `${rankData.progressToNext}%` }}
                        />
                      </div>
                      <div className="text-xs text-tech-text-muted mt-1">
                        {(rankData.nextRank.minScore - rankData.score).toLocaleString()} points to next rank
                      </div>
                    </div>
                  )}
                  {!rankData.nextRank && (
                    <div className="mb-6 p-4 rounded-xl bg-amber-400/10 border border-amber-400/30 text-amber-300 text-center font-semibold">
                      🌐 Maximum rank achieved — Visionary
                    </div>
                  )}

                  {/* Badges */}
                  {rankData.earnedBadges.length > 0 && (
                    <div>
                      <p className="text-xs text-tech-text-muted uppercase tracking-wider font-semibold mb-3">Milestone Badges</p>
                      <div className="flex flex-wrap gap-2">
                        {rankData.earnedBadges.map((badge) => (
                          <span
                            key={badge.id}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border bg-gradient-to-br ${badge.color}`}
                            title={badge.description}
                          >
                            {badge.emoji} {badge.title}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Learn more link */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <Link
                      to="/ranking-guide"
                      className="inline-flex items-center gap-2 text-sm text-tech-cyan hover:text-tech-cyan/80 transition-colors"
                    >
                      Learn about the ranking system
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              )}

              {/* ── AI Builder Identity ── */}
              {hasAiShowcase && aiShowcase && (
                <div className="p-8 rounded-3xl glass-panel border border-tech-cyan/20">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-violet-600 flex items-center justify-center">
                      <BrainCircuit className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">AI Builder Identity</h2>
                      <p className="text-sm text-tech-text-muted">Tools, projects, and proof</p>
                    </div>
                  </div>

                  {aiShowcase.publicTasksStory?.trim() && (
                    <div className="mb-8">
                      <h3 className="text-xs font-semibold text-tech-cyan uppercase tracking-wider mb-2">Tasks &amp; Narrative</h3>
                      <p className="text-tech-text-muted whitespace-pre-wrap leading-relaxed">{aiShowcase.publicTasksStory.trim()}</p>
                    </div>
                  )}

                  {aiShowcase.aiToolExperiences?.some((e) => (e.toolName && e.toolName.trim()) || (e.story && e.story.trim())) && (
                    <div className="mb-8">
                      <h3 className="text-xs font-semibold text-tech-cyan uppercase tracking-wider mb-4">AI Tools &amp; Experience</h3>
                      <div className="grid gap-4 md:grid-cols-2">
                        {aiShowcase.aiToolExperiences
                          .filter((e) => (e.toolName && e.toolName.trim()) || (e.story && e.story.trim()))
                          .map((exp, idx) => (
                            <div key={`${exp.toolName}-${idx}`} className="rounded-2xl border border-white/8 bg-white/4 p-5">
                              {exp.toolName?.trim() && (
                                <p className="font-semibold text-tech-text mb-2 flex items-center gap-2">
                                  <Zap className="w-4 h-4 text-amber-400" />
                                  {exp.toolName.trim()}
                                </p>
                              )}
                              {exp.story?.trim() && (
                                <p className="text-sm text-tech-text-muted whitespace-pre-wrap leading-relaxed">{exp.story.trim()}</p>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {aiShowcase.featuredLinks && aiShowcase.featuredLinks.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-tech-cyan uppercase tracking-wider mb-4">Featured Links</h3>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {aiShowcase.featuredLinks.map((link, idx) => (
                          <LinkPreviewCard
                            key={`${link.url}-${idx}`}
                            url={link.url}
                            title={link.title}
                            description={link.description}
                            imageUrl={link.imageUrl}
                            siteName={link.siteName}
                            variant="purple"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Task Accomplishments ── */}
              {(userTasks.length > 0 || loadingTasks) && (
                <div className="p-8 rounded-3xl glass-panel border border-emerald-500/20">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 flex items-center justify-center">
                        <Target className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">Task Accomplishments</h2>
                        <p className="text-sm text-tech-text-muted">Public tasks by {displayName}</p>
                      </div>
                    </div>
                    <span className="hidden sm:inline-flex px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-xs font-bold text-emerald-400 uppercase tracking-wider">
                      {userTasks.length} Tasks
                    </span>
                  </div>

                  {loadingTasks ? (
                    <div className="flex justify-center py-8">
                      <Loader className="w-8 h-8 text-emerald-400 animate-spin" />
                    </div>
                  ) : (
                    <div className="flex gap-5 overflow-x-auto custom-scrollbar snap-x snap-mandatory pb-4">
                      {userTasks.slice(0, 20).map((task) => (
                        <div
                          key={task.id}
                          className="snap-center w-[300px] md:w-[360px] shrink-0 p-5 rounded-2xl border border-white/8 bg-white/4 hover:border-emerald-400/40 hover:shadow-[0_0_30px_rgba(16,185,129,0.12)] transition-all duration-300 group flex flex-col justify-between"
                        >
                          <div>
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1 min-w-0 pr-3">
                                <h3 className="font-bold text-tech-text mb-1.5 truncate group-hover:text-emerald-300 transition-colors">{task.title}</h3>
                                {task.category && (
                                  <span className="inline-block px-2 py-0.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-semibold uppercase tracking-wider">
                                    {task.category}
                                  </span>
                                )}
                              </div>
                              <Link
                                to={`/marketplace?task=${task.id}`}
                                className="w-9 h-9 rounded-xl bg-white/5 hover:bg-emerald-500/20 text-tech-text-muted hover:text-emerald-300 border border-white/8 hover:border-emerald-500/30 transition-all flex items-center justify-center shrink-0"
                              >
                                <Eye className="w-4 h-4" />
                              </Link>
                            </div>
                            <p className="text-tech-text-muted text-sm mb-4 line-clamp-2 leading-relaxed">
                              {task.summary || task.requirements}
                            </p>
                          </div>

                          <div>
                            <div className="flex flex-wrap gap-1.5 mb-4">
                              {task.tags && task.tags.slice(0, 3).map(tag => (
                                <span key={tag} className="px-2 py-0.5 rounded bg-white/6 border border-white/8 text-xs text-tech-text-muted">
                                  {tag}
                                </span>
                              ))}
                            </div>
                            <div className="flex items-center justify-between text-xs text-tech-text-muted pt-3 border-t border-white/8">
                              {task.createdAt && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5 text-tech-cyan" />
                                  <span>{new Date(task.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/5">
                                <Activity className={`w-3 h-3 ${task.status === 'completed' ? 'text-emerald-400' : 'text-amber-400'}`} />
                                <span className="capitalize">{task.status?.replace('-', ' ')}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Analytics (Own Profile) ── */}
              {isOwnProfile && dashboardData && (
                <div className="p-8 rounded-3xl glass-panel border border-white/8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold">Profile Analytics</h2>
                  </div>

                  {loadingAnalytics ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader className="w-8 h-8 text-tech-cyan animate-spin" />
                    </div>
                  ) : (
                    <div className="space-y-8">
                      {dashboardData.analytics?.skillsDistribution && Object.keys(dashboardData.analytics.skillsDistribution).length > 0 && (
                        <div>
                          <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
                            <PieChartIcon className="w-5 h-5 text-tech-cyan" />
                            Skills Distribution
                          </h3>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={Object.entries(dashboardData.analytics.skillsDistribution).map(([name, value]) => ({
                                    name,
                                    value: Number(value),
                                  }))}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }: { name?: string; percent?: number }) => `${name || 'Unknown'} ${percent ? (percent * 100).toFixed(0) : 0}%`}
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {Object.keys(dashboardData.analytics.skillsDistribution).map((_, index) => (
                                    <Cell key={`cell-${index}`} fill={['#22d3ee', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'][index % 5]} />
                                  ))}
                                </Pie>
                                <Tooltip />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                      {dashboardData.performanceTrends && (
                        <div>
                          <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
                            <Activity className="w-5 h-5 text-green-400" />
                            Performance Trends
                          </h3>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={dashboardData.performanceTrends.tasksCompleted || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                <XAxis dataKey="date" stroke="rgba(255,255,255,0.3)" />
                                <YAxis stroke="rgba(255,255,255,0.3)" />
                                <Tooltip contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }} />
                                <Legend />
                                <Line type="monotone" dataKey="value" stroke="#22d3ee" strokeWidth={2} name="Tasks Completed" dot={{ fill: '#22d3ee' }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                      {dashboardData.taskAchieverProgress && (
                        <div>
                          <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-amber-400" />
                            TaskAchiever Progress
                          </h3>
                          <div className="space-y-4">
                            {Object.entries(dashboardData.taskAchieverProgress).map(([key, progress]: [string, any]) => (
                              <div key={key}>
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="text-tech-text-muted capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                                  <span className="font-medium text-tech-text">{progress.current} / {progress.required}</span>
                                </div>
                                <div className="w-full bg-white/8 rounded-full h-2 overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-tech-cyan to-blue-400 transition-all"
                                    style={{ width: `${Math.min((progress.current / progress.required) * 100, 100)}%` }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── Skills ── */}
              {(profile.skills.primary.length > 0 || profile.skills.secondary.length > 0) && (
                <div className="p-8 rounded-3xl glass-panel border border-amber-500/15">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold">Skills &amp; Expertise</h2>
                  </div>
                  {profile.skills.primary.length > 0 && (
                    <div className="mb-6">
                      <p className="text-xs text-tech-text-muted uppercase tracking-wider mb-3">Primary Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {profile.skills.primary.map((skill) => (
                          <span key={skill} className="px-4 py-2 rounded-xl bg-amber-500/10 border border-amber-400/20 text-sm font-medium flex items-center gap-2">
                            <Star className="w-4 h-4 text-amber-400" />
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {profile.skills.secondary.length > 0 && (
                    <div>
                      <p className="text-xs text-tech-text-muted uppercase tracking-wider mb-3">Secondary Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {profile.skills.secondary.map((skill) => (
                          <span key={skill} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Specialties ── */}
              {profile.specialties.length > 0 && (
                <div className="p-8 rounded-3xl glass-panel border border-pink-500/15">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold">Specialties</h2>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {profile.specialties.map((specialty) => (
                      <span key={specialty} className="px-4 py-2 rounded-xl bg-pink-500/10 border border-pink-400/20 text-sm font-medium flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-pink-400" />
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Portfolio Projects ── */}
              {profile.portfolio.projects && profile.portfolio.projects.length > 0 && (
                <div className="p-8 rounded-3xl glass-panel border border-white/8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 flex items-center justify-center">
                      <FolderOpen className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold">Portfolio Projects</h2>
                  </div>
                  <div className="space-y-4">
                    {profile.portfolio.projects.map((project) => {
                      const files = getProjectFiles(project);
                      const isExpanded = expandedProject === project.title;
                      return (
                        <div key={project.title} className="rounded-2xl border border-white/8 bg-white/4 p-5 cursor-pointer hover:border-tech-cyan/30 transition-all"
                          onClick={() => setExpandedProject(isExpanded ? null : project.title)}>
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-bold text-tech-text">{project.title}</h3>
                            {project.url && (
                              <a href={project.url} target="_blank" rel="noopener noreferrer"
                                className="ml-3 shrink-0 text-tech-cyan hover:text-tech-cyan/80 transition-colors"
                                onClick={e => e.stopPropagation()}>
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                          {project.description && (
                            <p className={`text-sm text-tech-text-muted leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
                              {project.description}
                            </p>
                          )}
                          {isExpanded && files.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {files.map((file, fi) => (
                                <FileBadge key={fi} url={file.url} fileName={file.fileName} type={file.type} />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              {/* Work Preferences */}
              <div className="p-6 rounded-2xl glass-panel border border-white/8">
                <h3 className="text-base font-semibold mb-4">Work Preferences</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-tech-text-muted">Availability</span>
                    <span className="capitalize text-tech-text">{profile.workPreferences.availability.replace('-', ' ')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-tech-text-muted">Location</span>
                    <span className="capitalize text-tech-text">{profile.workPreferences.location.replace('-', ' ')}</span>
                  </div>
                  {profile.workPreferences.hourlyRate && (
                    <div className="flex items-center justify-between">
                      <span className="text-tech-text-muted">Rate</span>
                      <span className="text-tech-text">{profile.workPreferences.currency || 'USD'} {profile.workPreferences.hourlyRate}/hr</span>
                    </div>
                  )}
                  {profile.workPreferences.preferredHoursPerWeek && (
                    <div className="flex items-center justify-between">
                      <span className="text-tech-text-muted">Hours/Week</span>
                      <span className="text-tech-text">{profile.workPreferences.preferredHoursPerWeek}h</span>
                    </div>
                  )}
                  {profile.workPreferences.fastTrackAvailable && (
                    <div className="flex items-center gap-2 pt-1">
                      <Zap className="w-4 h-4 text-amber-400" />
                      <span className="text-amber-300 text-xs font-semibold">Fast-track available</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Metrics */}
              <div className="p-6 rounded-2xl glass-panel border border-white/8">
                <h3 className="text-base font-semibold mb-4">Performance</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Tasks delivered', value: profile.metrics.tasksCompleted, icon: Target },
                    { label: 'SLA reliability', value: `${profile.metrics.slaReliability}%`, icon: Shield },
                    { label: 'Avg turnaround', value: `${profile.metrics.averageTurnaroundHours.toFixed(1)}h`, icon: Clock },
                    { label: 'Quality score', value: `${profile.metrics.qualityScore.toFixed(1)}/5`, icon: Star },
                  ].map((item) => (
                    <div key={item.label} className="p-3 rounded-xl bg-white/5 border border-white/8 flex flex-col gap-1.5">
                      <item.icon className="w-4 h-4 text-tech-cyan" />
                      <span className="text-base font-bold text-tech-text">{item.value}</span>
                      <span className="text-xs text-tech-text-muted leading-tight">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              {profile.skills.certifications && profile.skills.certifications.length > 0 && (
                <div className="p-6 rounded-2xl glass-panel border border-white/8">
                  <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-tech-cyan" />
                    Certifications
                  </h3>
                  <div className="space-y-2">
                    {profile.skills.certifications.slice(0, 3).map((cert) => (
                      <div key={cert.id} className="p-3 rounded-xl bg-white/5 border border-white/8">
                        <div className="font-medium text-sm text-tech-text">{cert.name}</div>
                        <div className="text-xs text-tech-text-muted">{cert.issuer}</div>
                        {cert.credentialUrl && (
                          <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-tech-cyan hover:text-tech-cyan/80 mt-1 inline-block">
                            Verify →
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Social Links */}
              {(profile.professionalInfo.linkedInUrl || profile.professionalInfo.githubUrl || profile.professionalInfo.portfolioUrl) && (
                <div className="p-6 rounded-2xl glass-panel border border-white/8">
                  <h3 className="text-base font-semibold mb-4">Links</h3>
                  <div className="flex flex-col gap-2">
                    {profile.professionalInfo.linkedInUrl && (
                      <LinkBadge url={profile.professionalInfo.linkedInUrl} label="LinkedIn" icon={<Linkedin className="w-4 h-4 text-blue-400" />} />
                    )}
                    {profile.professionalInfo.githubUrl && (
                      <LinkBadge url={profile.professionalInfo.githubUrl} label="GitHub" icon={<Github className="w-4 h-4 text-tech-text" />} />
                    )}
                    {profile.professionalInfo.portfolioUrl && (
                      <LinkBadge url={profile.professionalInfo.portfolioUrl} label="Portfolio" icon={<Globe className="w-4 h-4 text-violet-400" />} />
                    )}
                  </div>
                </div>
              )}

              {/* Contact */}
              <div className="p-6 rounded-2xl glass-panel border border-white/8">
                <h3 className="text-base font-semibold mb-4">Contact</h3>
                <div className="flex flex-col gap-2">
                  <a href={`mailto:${profile.userEmail}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/8 transition-all text-sm">
                    <Mail className="w-4 h-4 text-tech-cyan" />
                    <span className="truncate">{profile.userEmail}</span>
                  </a>
                  {profile.personalInfo.phone && (
                    <a href={`tel:${profile.personalInfo.phone}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/8 transition-all text-sm">
                      <Phone className="w-4 h-4 text-tech-cyan" />
                      {profile.personalInfo.phone}
                    </a>
                  )}
                </div>
              </div>

              {/* Join CTA (non-own profiles) */}
              {!isOwnProfile && (
                <div className="p-6 rounded-2xl border border-tech-cyan/20 bg-gradient-to-br from-tech-cyan/5 to-blue-500/5 text-center">
                  <div className="text-3xl mb-2">🌐</div>
                  <h3 className="font-bold text-tech-text mb-1">Join GNK Continuum</h3>
                  <p className="text-sm text-tech-text-muted mb-4">Build your rank, showcase your AI work, connect with builders worldwide.</p>
                  <Link
                    to="/signup"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-tech-blue to-tech-cyan text-white font-semibold text-sm hover:opacity-90 transition-opacity"
                  >
                    Get Started Free
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Photo Edit Modal ── */}
      {showEditModal && isOwnProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowEditModal(false)} />
          <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-tech-surface/95 backdrop-blur-xl shadow-2xl p-8">
            <button onClick={() => setShowEditModal(false)} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 transition-colors">
              <X className="w-5 h-5 text-tech-text-muted" />
            </button>
            <h2 className="text-xl font-bold mb-6 text-tech-text">Update Profile Photo</h2>

            {imagePreview && (
              <div className="mb-6 flex justify-center">
                <img src={imagePreview} alt="Preview" className="w-32 h-32 rounded-2xl object-cover border-2 border-tech-cyan/40" />
              </div>
            )}

            <div className="space-y-4">
              <label className="flex flex-col items-center gap-3 p-6 rounded-2xl border-2 border-dashed border-tech-cyan/30 hover:border-tech-cyan/60 transition-colors cursor-pointer bg-tech-cyan/5">
                <Upload className="w-8 h-8 text-tech-cyan" />
                <span className="text-tech-text-muted text-sm">Click to select image (max 5MB)</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
              </label>

              <div className="flex gap-3">
                <button
                  onClick={handlePhotoUpload}
                  disabled={!selectedImage || uploadingPhoto}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-tech-blue to-tech-cyan text-white font-semibold disabled:opacity-50 transition-opacity hover:opacity-90 flex items-center justify-center gap-2"
                >
                  {uploadingPhoto ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                </button>
                {profile?.personalInfo.profilePhoto && (
                  <button
                    onClick={handlePhotoRemove}
                    disabled={uploadingPhoto}
                    className="px-4 py-3 rounded-xl border border-red-400/30 bg-red-500/10 text-red-400 font-semibold disabled:opacity-50 hover:bg-red-500/20 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasker;
